import React, { useEffect, useState } from "react";
import OurLayouts from "../layout";
import { Switch } from "antd";
import { toast } from "react-toastify";

export default function AdminList() {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingFeatureFlag, setUpdatingFeatureFlag] = useState(null);
  const API_BASE = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const token = localStorage.getItem("auth_token");
        const response = await fetch(`${API_BASE}auth/fetch-user-details?role=ADMIN`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        setAdmins(list);
      } catch (e) {
        setError("Failed to load admin list");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdmins();
  }, [API_BASE]);

  const handleToggleStatus = async (admin) => {
    if (!admin?.id) return;
    const token = localStorage.getItem("auth_token");
    const tenantId = admin?.tenantId; // Use tenantId coming from the admin record
    const nextActive = !(admin.active === true);
    try {
      setUpdatingId(admin.id);
      // First call with tenantId
      const res1 = await fetch(`${API_BASE}auth/update-user-details/${admin.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          tenantId: tenantId,
        },
        body: JSON.stringify({ active: nextActive }),
      });

      if (res1.ok) {
        // Second call without tenantId
        const res2 = await fetch(`${API_BASE}auth/update-user-details/${admin.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ active: nextActive }),
        });

        if (res2.ok) {
          setAdmins((prev) => (prev || []).map((a) => (a.id === admin.id ? { ...a, active: nextActive } : a)));
          toast.success(`Admin ${nextActive ? "activated" : "deactivated"}`);
        } else {
          throw new Error("Second API call failed");
        }
      } else {
        throw new Error("First API call failed");
      }
    } catch (e) {
      console.error(e);
      toast.error("Unable to update admin status");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleFeatureFlagToggle = async (admin) => {
    if (!admin?.id) return;
    const token = localStorage.getItem("auth_token");
    const clinicId = localStorage.getItem("clinicId") || "clinic1";
    const nextStatus = !(admin.featureFlag === true);
    
    try {
      setUpdatingFeatureFlag(admin.id);
      
      const response = await fetch(`${API_BASE}feature-flags/68edf17edaa367229272b2e3`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          serviceName: "ABDM",
          clinicId: clinicId,
          status: nextStatus
        }),
      });

      if (response.ok) {
        setAdmins((prev) => (prev || []).map((a) => (a.id === admin.id ? { ...a, featureFlag: nextStatus } : a)));
        toast.success(`Feature flag ${nextStatus ? "activated" : "deactivated"}`);
      } else {
        throw new Error("Failed to update feature flag");
      }
    } catch (e) {
      console.error(e);
      toast.error("Unable to update feature flag");
    } finally {
      setUpdatingFeatureFlag(null);
    }
  };

  return (
    <OurLayouts>
    <div className="p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-[#004276]">Admin List</h1>
      </div>

      {isLoading && (
        <div className="text-gray-600">Loading...</div>
      )}
      {error && (
        <div className="text-red-600 text-sm mb-3">{error}</div>
      )}

      {!isLoading && admins.length === 0 && !error && (
        <div className="text-gray-600">No admins found.</div>
      )}

      {!isLoading && admins.length > 0 && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feature Flag</th> */}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {admins.map((admin) => {
                  const nameParts = [admin.firstName, admin.middleName, admin.lastName]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <tr key={admin.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{nameParts || "-"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{admin.mobile || "-"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{admin.email || "-"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{admin.active==true ? "Active" : "Inactive" || "-"}</div>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full ${admin.featureFlag ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                            {admin.featureFlag ? "Active" : "Inactive"}
                          </span>
                          <Switch
                            checked={!!admin.featureFlag}
                            loading={updatingFeatureFlag === admin.id}
                            onChange={() => handleFeatureFlagToggle(admin)}
                            size="small"
                          />
                        </div>
                      </td> */}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
    </OurLayouts>
  );
}


